'use client'

import ConfirmModal from '@/components/confirm-modal'
import { CatalogSelector } from '@/components/CatalogSelector'
import PaginationControls from '@/components/pagination/PaginationControls'
import CatalogFormPage from '@/pages/admin/bookings/catalog/catalog-form-page'
import {
    addCatalogHospital,
    addCatalogPartner,
    addCatalogProfessional,
    Catalog,
    CatalogCategory,
    catalogCompanyPrice,
    CatalogLinkEntityType,
    getCatalog,
    getLinkedCatalogItems,
    removeCatalogHospital,
    removeCatalogPartner,
    removeCatalogProfessional,
    updateCatalogPartner,
    updateCatalogProfessional,
} from '@/lib/api/catalog/catalog-api'
import {
    applyPriceFieldChange,
    formatCentsToDisplay,
    parseCurrencyToCents,
    PriceDraft,
} from '@/lib/api/catalog/catalog-price-format'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useCatalogAccess } from '@/lib/hooks/use-catalog-access'
import { useCatalogCategoryLabels } from '@/lib/hooks/use-service-label'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAdd,
    MdCallSplit,
    MdDelete,
    MdExpandLess,
    MdExpandMore,
    MdLocalHospital,
    MdSave,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface Props {
    entityType: CatalogLinkEntityType
    entityId: number
    companyId: number
    // Nome/local da entidade (para pré-vincular no modal de criação de item novo).
    entityName?: string
    entityLocation?: string
}

// Categorias que ganham botão "Adicionar" no topo (só parceiro/profissional).
// O rótulo vem do hook: fora da saúde, Procedimento vira Pedido.
const ADDABLE_CATEGORIES: CatalogCategory[] = [
    'CONSULTATION',
    'EXAM',
    'PROCEDURE',
]

const ENTITY_LABEL: Record<CatalogLinkEntityType, string> = {
    partner: 'parceiro',
    professional: 'profissional',
    hospital: 'hospital',
}

// Espelha PROFESSIONAL_PRICE_REQUIRED_CATEGORIES do backend: em procedimento o valor do
// profissional é dinâmico (sai por cirurgia, no agendamento), então o vínculo pode nascer
// sem preço. Nas demais categorias o backend recusa vínculo sem custo e sem venda.
const PROFESSIONAL_PRICE_REQUIRED_CATEGORIES: CatalogCategory[] = [
    'EXAM',
    'CONSULTATION',
    'TELECONSULTATION',
]

interface EditDraft extends PriceDraft {
    maxDiscount: string
}

const emptyDraft = (): EditDraft => ({
    price: '',
    sellingPrice: '',
    markupPercentage: '',
    maxDiscount: '',
})

// Extrai o vínculo da entidade consultada dentro de um item. Parceiro/profissional têm
// preço próprio; hospital é apenas associativo.
//
// A listagem vinculada traz só um elemento em partners/professionals/hospitals, mas o item
// recém-criado/atrelado é buscado por getCatalog, que traz TODOS os vínculos — ali o [0]
// pode ser de outra entidade e exibiria o preço errado. Por isso procuramos pelo entityId,
// caindo no [0] apenas quando a lista já vem filtrada pelo backend.
function entityLink(
    item: Catalog,
    entityType: CatalogLinkEntityType,
    entityId: number
) {
    if (entityType === 'partner') {
        return (
            item.partners?.find(p => p.partnerId === entityId) ??
            item.partners?.[0]
        )
    }
    if (entityType === 'professional') {
        return (
            item.professionals?.find(p => p.professionalId === entityId) ??
            item.professionals?.[0]
        )
    }
    return item.hospitals?.find(h => h.hospitalId === entityId) ?? item.hospitals?.[0]
}

// Campos de preço do vínculo. Usado nos dois lugares: no bloco do item pendente (antes de
// atrelar) e na edição de um item já vinculado.
function PriceFields({
    draft,
    onPriceField,
    onMaxDiscount,
}: {
    draft: EditDraft
    onPriceField: (
        field: 'price' | 'sellingPrice' | 'markupPercentage',
        value: string
    ) => void
    onMaxDiscount: (value: string) => void
}) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
                <label className="block text-xs font-medium mb-1 text-base-content/70">
                    Preço de Custo
                </label>
                <input
                    type="text"
                    value={draft.price}
                    onChange={e => onPriceField('price', e.target.value)}
                    placeholder="R$ 0,00"
                    className="input input-bordered input-sm w-full"
                />
            </div>
            <div>
                <label className="block text-xs font-medium mb-1 text-base-content/70">
                    Preço de Venda
                </label>
                <input
                    type="text"
                    value={draft.sellingPrice}
                    onChange={e => onPriceField('sellingPrice', e.target.value)}
                    placeholder="R$ 0,00"
                    className="input input-bordered input-sm w-full"
                />
            </div>
            <div>
                <label className="block text-xs font-medium mb-1 text-base-content/70">
                    Markup (%)
                </label>
                <input
                    type="number"
                    step="0.01"
                    value={draft.markupPercentage}
                    onChange={e =>
                        onPriceField('markupPercentage', e.target.value)
                    }
                    placeholder="0"
                    className="input input-bordered input-sm w-full"
                />
            </div>
            <div>
                <label className="block text-xs font-medium mb-1 text-base-content/70">
                    Desconto Máximo (%)
                </label>
                <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={draft.maxDiscount}
                    onChange={e => onMaxDiscount(e.target.value)}
                    placeholder="0"
                    className="input input-bordered input-sm w-full"
                />
            </div>
        </div>
    )
}

export function EntityCatalogTab({
    entityType,
    entityId,
    companyId,
    entityName,
    entityLocation,
}: Props) {
    const label = ENTITY_LABEL[entityType]
    const hasPrice = entityType !== 'hospital'
    const categoryLabels = useCatalogCategoryLabels()
    // Só oferece criar o que o usuário vê no menu.
    const { isCategoryAllowed } = useCatalogAccess()
    const addableCategories = ADDABLE_CATEGORIES.filter(isCategoryAllowed)

    const [searchInput, setSearchInput] = useState('')
    const debouncedSearch = useDebounce(searchInput, 300)

    const [expandedId, setExpandedId] = useState<number | null>(null)
    const [draft, setDraft] = useState<EditDraft>(emptyDraft())
    const [saving, setSaving] = useState(false)
    const [attaching, setAttaching] = useState(false)
    const [removeTarget, setRemoveTarget] = useState<Catalog | null>(null)
    const [removing, setRemoving] = useState(false)
    // Modal de criação de item novo já atrelado à entidade (parceiro/profissional).
    const [createCategory, setCreateCategory] =
        useState<CatalogCategory | null>(null)
    // Item recém-criado/atrelado: fica fixado no topo da lista e já expandido para receber
    // o preço. Sem isso ele se perderia na ordenação (a lista é paginada e ordenada pelo
    // backend), podendo cair em outra página — e o usuário acabaria de cadastrar um item
    // que não consegue ver.
    const [pinnedItem, setPinnedItem] = useState<Catalog | null>(null)
    // Item escolhido no seletor que ainda NÃO foi atrelado. O vínculo de parceiro/
    // profissional só nasce no "Salvar", já com preço: o backend recusa vínculo sem custo e
    // sem venda (sem custo o repasse sai a menos, sem venda o item não tem valor a cobrar),
    // então não dá para atrelar em branco e preencher depois.
    const [pendingItem, setPendingItem] = useState<Catalog | null>(null)
    const [pendingDraft, setPendingDraft] = useState<EditDraft>(emptyDraft())

    const filters = useMemo(
        () => ({ entityType, entityId, search: debouncedSearch || undefined }),
        [entityType, entityId, debouncedSearch]
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) =>
            getLinkedCatalogItems(companyId, {
                entityType: params.entityType,
                entityId: params.entityId,
                search: params.search,
                page: params.page,
                limit: params.limit,
            }),
        [companyId]
    )

    const {
        items,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
        reloadSilent,
    } = usePaginatedPage<Catalog, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!companyId && !!entityId,
    })

    const draftFromItem = (item: Catalog): EditDraft => {
        const link = entityLink(item, entityType, entityId) as
            | {
                  price?: number | null
                  sellingPrice?: number | null
                  markupPercentage?: number | null
                  maxDiscount?: number | null
              }
            | undefined
        return {
            price: formatCentsToDisplay(link?.price ?? undefined),
            sellingPrice: formatCentsToDisplay(link?.sellingPrice ?? undefined),
            markupPercentage:
                link?.markupPercentage != null
                    ? String(link.markupPercentage)
                    : '',
            maxDiscount:
                link?.maxDiscount != null ? String(link.maxDiscount) : '',
        }
    }

    const startEdit = (item: Catalog) => {
        if (expandedId === item.id) {
            setExpandedId(null)
            return
        }
        setExpandedId(item.id)
        setDraft(draftFromItem(item))
    }

    // Traz o item recém-criado/atrelado para o topo da lista, apenas destacado. Ele NÃO
    // nasce expandido: o preço já foi informado antes de atrelar, e reabrir o formulário
    // preenchido faz o operador achar que não salvou e clicar em salvar de novo.
    const focusNewItem = useCallback(
        async (catalogId: number) => {
            try {
                const res = await getCatalog(catalogId)
                if (!res.success || !res.result) return
                setPinnedItem(res.result)
                setExpandedId(null)
            } catch {
                // Falha ao destacar não deve travar o fluxo: o item já foi vinculado e
                // aparece na lista normalmente após o reload.
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityType, entityId]
    )

    // O item destacado vai para o topo. Ele pode ou não estar na página atual (a ordenação
    // é do backend), então é removido do meio da lista para não aparecer duas vezes.
    const displayItems = useMemo(() => {
        if (!pinnedItem) return items
        return [pinnedItem, ...items.filter(i => i.id !== pinnedItem.id)]
    }, [items, pinnedItem])

    // Buscar ou trocar de página é o usuário saindo do fluxo "acabei de cadastrar":
    // o destaque some e a lista volta a ser só o que o backend devolveu.
    const clearPinned = () => setPinnedItem(null)

    const handleSearchChange = (value: string) => {
        setSearchInput(value)
        clearPinned()
    }

    const handlePageChange = (nextPage: number) => {
        setPage(nextPage)
        clearPinned()
    }

    const onPriceField = (
        field: 'price' | 'sellingPrice' | 'markupPercentage',
        value: string
    ) => {
        setDraft(prev => ({
            ...applyPriceFieldChange(prev, field, value),
            maxDiscount: prev.maxDiscount,
        }))
    }

    const handleSave = async (item: Catalog) => {
        setSaving(true)
        try {
            const data = {
                price: parseCurrencyToCents(draft.price),
                sellingPrice: parseCurrencyToCents(draft.sellingPrice),
                markupPercentage: draft.markupPercentage
                    ? parseFloat(draft.markupPercentage)
                    : undefined,
                maxDiscount: draft.maxDiscount
                    ? parseFloat(draft.maxDiscount)
                    : undefined,
            }
            const res =
                entityType === 'partner'
                    ? await updateCatalogPartner(item.id, entityId, data)
                    : await updateCatalogProfessional(item.id, entityId, data)
            if (res.success) {
                toast.success('Preço atualizado')
                // Fecha o formulário: com ele aberto o operador não vê sinal de que gravou
                // (o toast some em segundos) e clica em salvar repetidamente.
                setExpandedId(null)
                reloadSilent()
            } else {
                toast.error(res.error || 'Falha ao atualizar preço')
            }
        } catch {
            toast.error('Falha ao atualizar preço')
        } finally {
            setSaving(false)
        }
    }

    // Hospital não tem preço próprio: o vínculo é só associativo e pode ser criado direto.
    // Parceiro/profissional passam pelo bloco de preço antes (ver `pendingItem`).
    const handleSelectToAttach = async (catalogId: number | null) => {
        if (!catalogId) return
        setAttaching(true)
        try {
            if (!hasPrice) {
                const res = await addCatalogHospital(catalogId, entityId)
                if (res.success) {
                    toast.success('Item atrelado')
                    reload()
                } else {
                    toast.error(res.error || 'Falha ao atrelar item')
                }
                return
            }

            const res = await getCatalog(catalogId)
            if (!res.success || !res.result) {
                toast.error(res.error || 'Falha ao carregar o item do catálogo')
                return
            }
            setPendingItem(res.result)
            setPendingDraft(emptyDraft())
        } catch {
            toast.error('Falha ao atrelar item')
        } finally {
            setAttaching(false)
        }
    }

    const onPendingPriceField = (
        field: 'price' | 'sellingPrice' | 'markupPercentage',
        value: string
    ) => {
        setPendingDraft(prev => ({
            ...applyPriceFieldChange(prev, field, value),
            maxDiscount: prev.maxDiscount,
        }))
    }

    // Cria o vínculo com o preço já preenchido.
    const handleSavePending = async () => {
        if (!pendingItem) return

        const price = parseCurrencyToCents(pendingDraft.price)
        const sellingPrice = parseCurrencyToCents(pendingDraft.sellingPrice)
        // Em procedimento o valor do profissional sai por cirurgia, no agendamento — ali o
        // vínculo pode nascer sem preço (mesma regra do backend).
        const priceRequired =
            entityType === 'partner' ||
            PROFESSIONAL_PRICE_REQUIRED_CATEGORIES.includes(pendingItem.category)
        if (priceRequired && (!price || !sellingPrice)) {
            toast.error(
                'Informe o preço de custo e o preço de venda antes de atrelar o item.'
            )
            return
        }

        setSaving(true)
        try {
            const data = {
                // Zero aqui é campo em branco (só chega neste ponto quando o preço é
                // opcional): grava vínculo sem preço em vez de vínculo com preço zerado.
                price: price || undefined,
                sellingPrice: sellingPrice || undefined,
                markupPercentage: pendingDraft.markupPercentage
                    ? parseFloat(pendingDraft.markupPercentage)
                    : undefined,
                maxDiscount: pendingDraft.maxDiscount
                    ? parseFloat(pendingDraft.maxDiscount)
                    : undefined,
            }
            const res =
                entityType === 'partner'
                    ? await addCatalogPartner(pendingItem.id, {
                          partnerId: entityId,
                          ...data,
                      })
                    : await addCatalogProfessional(pendingItem.id, {
                          professionalId: entityId,
                          ...data,
                      })
            if (res.success) {
                toast.success('Item atrelado')
                const catalogId = pendingItem.id
                setPendingItem(null)
                setPendingDraft(emptyDraft())
                reload()
                await focusNewItem(catalogId)
            } else {
                toast.error(res.error || 'Falha ao atrelar item')
            }
        } catch {
            toast.error('Falha ao atrelar item')
        } finally {
            setSaving(false)
        }
    }

    const cancelPending = () => {
        setPendingItem(null)
        setPendingDraft(emptyDraft())
    }

    // Ao fechar o modal (salvar, cancelar ou clicar fora) SEMPRE recarrega a
    // lista: uma edição/atrelamento feito lá dentro precisa refletir aqui.
    // `createdId` só vem quando o item foi de fato salvo — aí ele é destacado no topo.
    const closeCreateModal = async (createdId?: number) => {
        setCreateCategory(null)
        reload()
        if (createdId) await focusNewItem(createdId)
    }

    const handleRemove = async () => {
        if (!removeTarget) return
        setRemoving(true)
        try {
            const res =
                entityType === 'partner'
                    ? await removeCatalogPartner(removeTarget.id, entityId)
                    : entityType === 'professional'
                      ? await removeCatalogProfessional(
                            removeTarget.id,
                            entityId
                        )
                      : await removeCatalogHospital(removeTarget.id, entityId)
            if (res.success) {
                toast.success('Item desvinculado')
                if (expandedId === removeTarget.id) setExpandedId(null)
                setRemoveTarget(null)
                reload()
            } else {
                toast.error(res.error || 'Falha ao desvincular item')
            }
        } catch {
            toast.error('Falha ao desvincular item')
        } finally {
            setRemoving(false)
        }
    }

    return (
        <div className="space-y-4">
            {/* Criar item novo já atrelado (só parceiro/profissional têm preço próprio) */}
            {hasPrice && addableCategories.length > 0 && (
                <div className="bg-base-200/50 rounded-xl p-4">
                    <label className="block text-sm font-medium mb-2">
                        Adicionar novo item já atrelado a este {label}
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {addableCategories.map(category => (
                            <button
                                key={category}
                                type="button"
                                onClick={() => setCreateCategory(category)}
                                className="btn btn-sm btn-outline btn-secondary gap-1"
                            >
                                <MdAdd /> {categoryLabels[category]}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Atrelar a item existente */}
            <div className="bg-base-200/50 rounded-xl p-4">
                <label className="block text-sm font-medium mb-2">
                    Atrelar {label} a um item do catálogo já existente
                </label>
                {/* Sem `registerEnabled`: aqui a busca já varre o catálogo
                    inteiro (sem funil de parceiro) e a criação de item novo tem
                    caminho próprio logo acima, com o formulário completo. */}
                <CatalogSelector
                    value={pendingItem?.id ?? null}
                    onChange={catalogId => handleSelectToAttach(catalogId)}
                    disabled={attaching || saving}
                    placeholder="Buscar item do catálogo para atrelar..."
                />
                {attaching && (
                    <p className="text-xs text-base-content/60 mt-2">
                        Carregando...
                    </p>
                )}

                {/* Preço do vínculo antes de atrelar: o item só é vinculado ao salvar. */}
                {pendingItem && (
                    <div className="mt-3 border border-secondary/40 rounded-xl p-3 bg-base-100 space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">
                                {pendingItem.name}
                            </span>
                            <span className="badge badge-sm badge-ghost">
                                {categoryLabels[pendingItem.category]}
                            </span>
                            {pendingItem.isVariation && (
                                <span className="badge badge-sm badge-info gap-1">
                                    <MdCallSplit /> Variação
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-base-content/60">
                            Informe o preço deste {label} para o item. O vínculo
                            é criado ao salvar.
                        </p>
                        <PriceFields
                            draft={pendingDraft}
                            onPriceField={onPendingPriceField}
                            onMaxDiscount={value =>
                                setPendingDraft(prev => ({
                                    ...prev,
                                    maxDiscount: value,
                                }))
                            }
                        />
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={cancelPending}
                                disabled={saving}
                                className="btn btn-sm btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSavePending}
                                disabled={saving}
                                className="btn btn-sm btn-secondary gap-1"
                            >
                                <MdSave />{' '}
                                {saving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Busca */}
            <div className="relative">
                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-xl z-10" />
                <input
                    type="text"
                    value={searchInput}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder="Buscar item vinculado..."
                    className="input input-bordered w-full pl-10"
                />
            </div>

            {/* Lista */}
            {loading ? (
                <div className="flex justify-center py-10">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            ) : displayItems.length === 0 ? (
                <div className="text-center py-10 text-base-content/60">
                    Nenhum item do catálogo vinculado a este {label}.
                </div>
            ) : (
                <div className="space-y-2">
                    {displayItems.map(item => {
                        const link = entityLink(item, entityType, entityId) as any
                        const expanded = expandedId === item.id
                        const isPinned = pinnedItem?.id === item.id
                        const companyPrice = catalogCompanyPrice(
                            item,
                            companyId
                        )
                        const summaryCents = hasPrice
                            ? (link?.sellingPrice ?? link?.price ?? undefined)
                            : (companyPrice.sellingPrice ??
                              companyPrice.price ??
                              undefined)
                        return (
                            <div
                                key={item.id}
                                className={`border rounded-xl overflow-hidden bg-base-100 ${
                                    isPinned
                                        ? 'border-secondary ring-1 ring-secondary/30'
                                        : 'border-base-300'
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => startEdit(item)}
                                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-base-200/50 transition-colors"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium truncate">
                                                {item.name}
                                            </span>
                                            <span className="badge badge-sm badge-ghost">
                                                {
                                                    categoryLabels[
                                                        item.category
                                                    ]
                                                }
                                            </span>
                                            {item.tussCode && (
                                                <span className="badge badge-sm badge-outline">
                                                    TUSS {item.tussCode}
                                                </span>
                                            )}
                                            {item.isVariation && (
                                                <span className="badge badge-sm badge-info gap-1">
                                                    <MdCallSplit /> Variação
                                                </span>
                                            )}
                                            {isPinned && (
                                                <span className="badge badge-sm badge-secondary">
                                                    Novo
                                                </span>
                                            )}
                                        </div>
                                        {item.isVariation && item.parent && (
                                            <p className="text-xs text-base-content/50 mt-0.5 truncate">
                                                Variação de {item.parent.name}
                                            </p>
                                        )}
                                    </div>
                                    <span className="text-sm font-semibold whitespace-nowrap">
                                        {summaryCents
                                            ? `R$ ${(summaryCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                            : hasPrice
                                              ? 'Sem preço'
                                              : ''}
                                    </span>
                                    {expanded ? (
                                        <MdExpandLess className="text-xl text-base-content/50" />
                                    ) : (
                                        <MdExpandMore className="text-xl text-base-content/50" />
                                    )}
                                </button>

                                {expanded && (
                                    <div className="p-4 border-t border-base-300 bg-base-200/30 space-y-4">
                                        {hasPrice ? (
                                            <>
                                                <PriceFields
                                                    draft={draft}
                                                    onPriceField={onPriceField}
                                                    onMaxDiscount={value =>
                                                        setDraft(prev => ({
                                                            ...prev,
                                                            maxDiscount: value,
                                                        }))
                                                    }
                                                />
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setRemoveTarget(
                                                                item
                                                            )
                                                        }
                                                        className="btn btn-sm btn-ghost text-error gap-1"
                                                    >
                                                        <MdDelete /> Desvincular
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleSave(item)
                                                        }
                                                        disabled={saving}
                                                        className="btn btn-sm btn-secondary gap-1"
                                                    >
                                                        <MdSave />{' '}
                                                        {saving
                                                            ? 'Salvando...'
                                                            : 'Salvar'}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="text-sm text-base-content/60 flex items-center gap-2">
                                                    <MdLocalHospital className="text-lg" />
                                                    Vínculo sem preço próprio. O
                                                    preço do item é o da filial
                                                    {summaryCents
                                                        ? ` (R$ ${(summaryCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}).`
                                                        : '.'}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setRemoveTarget(item)
                                                    }
                                                    className="btn btn-sm btn-ghost text-error gap-1"
                                                >
                                                    <MdDelete /> Desvincular
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}

            {total > limit && (
                <PaginationControls
                    page={page}
                    limit={limit}
                    total={total}
                    loading={loading}
                    onPageChange={handlePageChange}
                    label="item"
                />
            )}

            {/* Modal de criação de item novo (reusa o CatalogFormPage em modo modal,
                com a entidade da aba pré-vinculada e travada). */}
            {createCategory && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-4xl max-h-[90vh] overflow-y-auto">
                        <CatalogFormPage
                            modalCreate
                            modalCategory={createCategory}
                            lockedPartner={
                                entityType === 'partner'
                                    ? {
                                          id: entityId,
                                          name: entityName || 'Parceiro',
                                          location: entityLocation,
                                      }
                                    : undefined
                            }
                            lockedProfessional={
                                entityType === 'professional'
                                    ? {
                                          id: entityId,
                                          name: entityName || 'Profissional',
                                      }
                                    : undefined
                            }
                            onSuccess={createdId => closeCreateModal(createdId)}
                            onCancel={() => closeCreateModal()}
                        />
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => closeCreateModal()}
                    />
                </div>
            )}

            <ConfirmModal
                isOpen={!!removeTarget}
                onClose={() => setRemoveTarget(null)}
                onConfirm={handleRemove}
                loading={removing}
                title="Desvincular item"
                message={`Remover o vínculo de "${removeTarget?.name ?? ''}" com este ${label}?`}
                confirmButtonText="Desvincular"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}

export default EntityCatalogTab
